import { apiRequest } from "../../api/apiClient";
import type {
  CurrentSpouse,
  CreateCurrentSpouseDto,
  UpdateCurrentSpouseDto,
} from "../../types/currentSpouse.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const currentSpouseService = {
  getCurrentSpousesByCertificateApplicationId: async (
    certificateApplicationId: number,
  ): Promise<CurrentSpouse[]> => {
    const response = await apiRequest(
      `/CurrentSpouses/certificate-application/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );

    if (response.status === 404 || response.status === 204) {
      return [];
    }

    const payload = await handleResponse<any>(response);

    if (Array.isArray(payload)) {
      return payload as CurrentSpouse[];
    }

    if (Array.isArray(payload?.data)) {
      return payload.data as CurrentSpouse[];
    }

    if (payload?.data && typeof payload.data === "object") {
      return [payload.data as CurrentSpouse];
    }

    if (payload && typeof payload === "object") {
      return [payload as CurrentSpouse];
    }

    return [];
  },

  createCurrentSpouse: async (
    data: CreateCurrentSpouseDto,
    photo?: File,
  ): Promise<CurrentSpouse> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, String(value));
      }
    });

    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest("/CurrentSpouses", {
      method: "POST",
      body: formData,
    });

    return handleResponse<CurrentSpouse>(response);
  },

  updateCurrentSpouse: async (
    data: UpdateCurrentSpouseDto,
    photo?: File,
  ): Promise<CurrentSpouse> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "profileImagePath") {
        formData.append(key, String(value));
      }
    });

    if (photo) {
      formData.append("photo", photo);
    }

    const response = await apiRequest("/CurrentSpouses", {
      method: "PUT",
      body: formData,
    });

    return handleResponse<CurrentSpouse>(response);
  },

  deleteCurrentSpouse: async (id: number): Promise<void> => {
    // Prefer resource delete route; fallback to body delete for API variants.
    let response = await apiRequest(`/CurrentSpouses/${id}`, {
      method: "DELETE",
    });

    if (response.status === 405) {
      response = await apiRequest("/CurrentSpouses", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
    }

    await handleResponse<void>(response);
  },
};
